import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useAuthStore } from './auth-store';
import React from 'react';
import { logActivity } from '@/lib/activity-logger';

export interface Favorite {
  id: string;
  name: string;
  category?: string;
  notes?: string;
  link?: string;
  store?: string;
  aisle?: string;
  price?: number;
  createdAt: string;
  updatedAt: string;
}

interface FavoriteState {
  favorites: Favorite[];
  isSyncing: boolean;
  fetchFromServer: (familyId?: string) => Promise<void>;
  createFavorite: (data: { name: string; category?: string; notes?: string; link?: string; store?: string; aisle?: string; price?: number }, familyId?: string) => Promise<Favorite | null>;
  updateFavorite: (id: string, updates: { name?: string; category?: string; notes?: string; link?: string; store?: string; aisle?: string; price?: number }, familyId?: string) => Promise<void>;
  deleteFavorite: (id: string, familyId?: string) => Promise<void>;
}

export const useFavoriteStore = create<FavoriteState>()(
  persist(
    (set, get) => ({
      favorites: [],
      isSyncing: false,

      fetchFromServer: async (familyId?: string) => {
        set({ isSyncing: true });
        try {
          const qs = familyId ? `?familyId=${familyId}` : '';
          const response = await apiGet(`/favorites${qs}`);
          if (response.success && response.data) {
            const data = response.data as { favorites?: Favorite[] };
            set({ favorites: data.favorites || [] });
          }
        } catch (error) {
          console.error('Failed to fetch favorites:', error);
        } finally {
          set({ isSyncing: false });
        }
      },

      createFavorite: async (data, familyId) => {
        const user = useAuthStore.getState().user;
        if (!user?.id) return null;

        const qs = familyId ? `?familyId=${familyId}` : '';

        const newFavorite: Favorite = {
          id: `fav-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          name: data.name,
          category: data.category || '',
          notes: data.notes || '',
          link: data.link || '',
          store: data.store || '',
          aisle: data.aisle || '',
          price: data.price || 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Optimistic update
        set((state) => ({
          favorites: [...state.favorites, newFavorite],
        }));

        try {
          const response = await apiPost(`/favorites${qs}`, data);
          if (response.success && response.data) {
            const responseData = response.data as { favorite: Favorite };
            logActivity({ action: 'ADD_FAVORITE', category: 'shopping', description: 'Favorite created', metadata: { favoriteId: responseData.favorite.id, name: data.name } });
            // Replace optimistic favorite with server response
            set((state) => ({
              favorites: state.favorites.map((f) =>
                f.id === newFavorite.id ? responseData.favorite : f
              ),
            }));
            return responseData.favorite;
          }
        } catch (error) {
          console.error('Failed to create favorite:', error);
          // Rollback optimistic update
          set((state) => ({
            favorites: state.favorites.filter((f) => f.id !== newFavorite.id),
          }));
        }
        return null;
      },

      updateFavorite: async (id, updates, familyId) => {
        const qs = familyId ? `?familyId=${familyId}` : '';

        const prevFavorites = get().favorites;

        // Optimistic update
        set((state) => ({
          favorites: state.favorites.map((f) =>
            f.id === id
              ? { ...f, ...updates, updatedAt: new Date().toISOString() }
              : f
          ),
        }));

        try {
          const response = await apiPut(`/favorites/${id}${qs}`, updates);
          if (!response.success) {
            // If 404: favorite no longer exists on server (stale cache) — auto-migrate by creating fresh
            if (response.error && response.error.includes('404')) {
              try {
                const prevFavorite = prevFavorites.find((f) => f.id === id);
                const createData = {
                  name: updates.name !== undefined ? updates.name : (prevFavorite?.name || ''),
                  category: updates.category !== undefined ? updates.category : prevFavorite?.category,
                  notes: updates.notes !== undefined ? updates.notes : prevFavorite?.notes,
                  link: updates.link !== undefined ? updates.link : prevFavorite?.link,
                  store: updates.store !== undefined ? updates.store : prevFavorite?.store,
                  aisle: updates.aisle !== undefined ? updates.aisle : prevFavorite?.aisle,
                  price: updates.price !== undefined ? updates.price : prevFavorite?.price,
                };
                const createResponse = await apiPost<{ favorite: Favorite }>(`/favorites${qs}`, createData);
                if (createResponse.success && createResponse.data) {
                  const newFav = (createResponse.data as { favorite: Favorite }).favorite;
                  set((state) => ({
                    favorites: state.favorites.map((f) => f.id === id ? newFav : f),
                  }));
                  logActivity({ action: 'ADD_FAVORITE', category: 'shopping', description: 'Favorite migrated and updated', metadata: { favoriteId: newFav.id } });
                  return;
                }
              } catch { /* fall through to rollback */ }
            }
            // Rollback on failure
            set({ favorites: prevFavorites });
          } else {
            logActivity({ action: 'ADD_FAVORITE', category: 'shopping', description: 'Favorite updated', metadata: { favoriteId: id } });
          }
        } catch (error) {
          console.error('Failed to update favorite:', error);
          set({ favorites: prevFavorites });
        }
      },

      deleteFavorite: async (id, familyId) => {
        const qs = familyId ? `?familyId=${familyId}` : '';

        const prevFavorites = get().favorites;

        // Optimistic delete
        set((state) => ({
          favorites: state.favorites.filter((f) => f.id !== id),
        }));

        try {
          const response = await apiDelete(`/favorites/${id}${qs}`);
          if (!response.success) {
            // Rollback on failure
            set({ favorites: prevFavorites });
          } else {
            logActivity({ action: 'REMOVE_FAVORITE', category: 'shopping', description: 'Favorite deleted', metadata: { favoriteId: id } });
          }
        } catch (error) {
          console.error('Failed to delete favorite:', error);
          set({ favorites: prevFavorites });
        }
      },
    }),
    {
      name: 'favorite-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useFavoriteHydrated = () => {
  const [hydrated, setHydrated] = React.useState(false);
  React.useEffect(() => {
    const unsubscribe = useFavoriteStore.persist.onFinishHydration(() => setHydrated(true));
    setHydrated(useFavoriteStore.persist.hasHydrated());
    return unsubscribe;
  }, []);
  return hydrated;
};
